import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Shield,
  ShieldAlert,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  FileCode,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react'
import { csrfHeaders } from '@/lib/csrf'
import { toast } from 'sonner'
import type { Project } from '@/types/conversations'

interface LiveSecurityAuditSectionProps {
  project: Project
  conversationId?: number
}

const SEVERITY_COLORS = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
} as const

const GRADE_COLORS: Record<string, string> = {
  'A+': 'text-green-600 border-green-300 bg-green-50 dark:bg-green-900/20',
  'A': 'text-green-600 border-green-300 bg-green-50 dark:bg-green-900/20',
  'B+': 'text-blue-600 border-blue-300 bg-blue-50 dark:bg-blue-900/20',
  'B': 'text-blue-600 border-blue-300 bg-blue-50 dark:bg-blue-900/20',
  'C+': 'text-yellow-600 border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20',
  'C': 'text-yellow-600 border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20',
  'D': 'text-orange-600 border-orange-300 bg-orange-50 dark:bg-orange-900/20',
  'F': 'text-red-600 border-red-300 bg-red-50 dark:bg-red-900/20',
}

const CATEGORY_KEYS: Record<string, string> = {
  secrets: 'secrets',
  dependencies: 'dependencies',
  django_config: 'djangoConfig',
  code_vulnerabilities: 'codeVulnerabilities',
  ai_review: 'aiReview',
}

export function LiveSecurityAuditSection({ project, conversationId }: LiveSecurityAuditSectionProps) {
  const { t } = useTranslation('conversations')
  const [starting, setStarting] = useState(false)
  const [includeAiReview, setIncludeAiReview] = useState(false)

  const auditStatus = project.security_audit_status
  const auditMetadata = project.security_audit_metadata
  const auditError = project.security_audit_error

  const handleStartAudit = async () => {
    if (!conversationId) {
      toast.error(t('live.security.noConversation'))
      return
    }

    setStarting(true)
    try {
      const workflowName = includeAiReview ? 'security_audit_with_ai' : 'security_audit'
      const response = await fetch(
        `/projects/${project.id}/features/${conversationId}/workflow/start`,
        {
          method: 'POST',
          headers: csrfHeaders(),
          body: JSON.stringify({ name: workflowName }),
        }
      )

      if (!response.ok) {
        const data = await response.json()
        toast.error(data.error || data.errors?.[0] || t('live.security.startFailed'))
      } else {
        toast.success(t('live.security.started'))
      }
    } catch (error) {
      console.error('Failed to start security audit:', error)
      toast.error(t('live.security.startFailed'))
    } finally {
      setStarting(false)
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium">{t('live.security.title')}</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('live.security.description')}
          </p>
        </div>
        {auditStatus && (
          <AuditStatusBadge status={auditStatus} />
        )}
      </div>

      {/* Error display */}
      {auditStatus === 'failed' && auditError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3">
          <div className="flex items-start gap-2">
            <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <p className="text-xs text-destructive">{auditError}</p>
          </div>
        </div>
      )}

      {/* AI review opt-in */}
      {auditStatus !== 'running' && (
        <button
          onClick={() => !starting && setIncludeAiReview(!includeAiReview)}
          disabled={starting}
          className={`w-full rounded-lg border p-3 text-left transition-colors ${
            includeAiReview
              ? 'border-violet-400 bg-violet-50 dark:border-violet-500 dark:bg-violet-950/20'
              : 'border-border hover:border-muted-foreground/30'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className={`h-4 w-4 ${includeAiReview ? 'text-violet-500' : 'text-muted-foreground'}`} />
              <span className="text-xs font-medium">{t('live.security.aiReview.title')}</span>
            </div>
            <Switch
              checked={includeAiReview}
              onCheckedChange={setIncludeAiReview}
              disabled={starting}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <p className="text-[10px] text-muted-foreground mt-1 ml-6">
            {t('live.security.aiReview.description')}
          </p>
        </button>
      )}

      {/* Action button */}
      {auditStatus !== 'running' && (
        <Button
          onClick={handleStartAudit}
          disabled={starting || !conversationId}
          size="sm"
          className="w-full"
        >
          {starting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              {t('live.security.starting')}
            </>
          ) : auditMetadata || auditStatus === 'completed' ? (
            <>
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('live.security.rerunButton')}
            </>
          ) : auditStatus === 'failed' ? (
            <>
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('live.security.retryButton')}
            </>
          ) : (
            <>
              <Shield className="h-4 w-4 mr-2" />
              {t('live.security.runButton')}
            </>
          )}
        </Button>
      )}

      {/* Running state */}
      {auditStatus === 'running' && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span>{t('live.security.running')}</span>
        </div>
      )}

      {/* Results */}
      {auditMetadata && <AuditResults metadata={auditMetadata} />}
    </div>
  )
}

function AuditResults({ metadata }: { metadata: NonNullable<Project['security_audit_metadata']> }) {
  const { t } = useTranslation('conversations')
  const trend = metadata.trend

  return (
    <div className="space-y-3">
      {/* Score + Grade */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {metadata.score >= 75 ? (
            <ShieldCheck className="h-5 w-5 text-green-600" />
          ) : metadata.score >= 40 ? (
            <Shield className="h-5 w-5 text-yellow-600" />
          ) : (
            <ShieldAlert className="h-5 w-5 text-red-600" />
          )}
          <span className="text-2xl font-bold">{metadata.score}</span>
          <span className="text-xs text-muted-foreground">/100</span>
        </div>
        <Badge
          variant="outline"
          className={GRADE_COLORS[metadata.grade] || 'text-muted-foreground'}
        >
          {metadata.grade}
        </Badge>
      </div>

      {/* Trend */}
      {trend && (
        <TrendDisplay diff={trend} />
      )}

      {/* Severity summary */}
      <div className="grid grid-cols-4 gap-1.5">
        {(['critical', 'high', 'medium', 'low'] as const).map(severity => (
          <div key={severity} className="rounded-md border p-2 text-center">
            <p className="text-lg font-semibold">{metadata.findings_count[severity]}</p>
            <p className="text-[10px] text-muted-foreground capitalize">
              {t(`live.security.results.${severity}`)}
            </p>
          </div>
        ))}
      </div>

      {/* Category breakdown */}
      <div className="space-y-1">
        {Object.entries(metadata.categories).map(([key, category]) => (
          <CategorySection
            key={key}
            name={CATEGORY_KEYS[key] ? t(`live.security.results.categories.${CATEGORY_KEYS[key]}`) : key}
            category={category}
          />
        ))}
      </div>

      {/* Timestamp */}
      {metadata.scanned_at && (
        <p className="text-[10px] text-muted-foreground">
          {t('live.security.results.lastRun', {
            date: new Date(metadata.scanned_at).toLocaleString(),
          })}
        </p>
      )}
    </div>
  )
}

function CategorySection({
  name,
  category,
}: {
  name: string
  category: NonNullable<Project['security_audit_metadata']>['categories'][string]
}) {
  const [expanded, setExpanded] = useState(false)
  const hasFindings = category.findings.length > 0

  return (
    <div className="rounded-md border">
      <button
        onClick={() => hasFindings && setExpanded(!expanded)}
        className="flex items-center justify-between w-full px-3 py-2 text-xs"
        disabled={!hasFindings}
      >
        <div className="flex items-center gap-2">
          {hasFindings ? (
            expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />
          ) : (
            <CheckCircle2 className="h-3 w-3 text-green-600" />
          )}
          <span className="font-medium">{name}</span>
        </div>
        <CategoryStatusBadge status={category.status} count={category.count} />
      </button>

      {expanded && hasFindings && (
        <div className="border-t px-3 py-2 space-y-2 max-h-48 overflow-y-auto">
          {category.findings.map(finding => (
            <FindingItem key={finding.id} finding={finding} />
          ))}
        </div>
      )}
    </div>
  )
}

function TrendDisplay({ diff }: { diff: NonNullable<NonNullable<Project['security_audit_metadata']>['trend']> }) {
  const { t } = useTranslation('conversations')

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      <span className="text-muted-foreground">
        {t('live.security.results.trend.previous', { score: diff.previous_score, grade: diff.previous_grade })}
      </span>
      {diff.score_change > 0 ? (
        <span className="flex items-center gap-1 text-green-600 font-medium">
          <TrendingUp className="h-3 w-3" />
          {t('live.security.results.trend.improved', { points: `+${diff.score_change}` })}
        </span>
      ) : diff.score_change < 0 ? (
        <span className="flex items-center gap-1 text-red-600 font-medium">
          <TrendingDown className="h-3 w-3" />
          {t('live.security.results.trend.declined', { points: diff.score_change })}
        </span>
      ) : (
        <span className="flex items-center gap-1 text-muted-foreground">
          <Minus className="h-3 w-3" />
          {t('live.security.results.trend.unchanged')}
        </span>
      )}
      {diff.fixed_count > 0 && (
        <span className="text-green-600">{t('live.security.results.trend.fixed', { count: diff.fixed_count })}</span>
      )}
      {diff.new_count > 0 && (
        <span className="text-orange-600">{t('live.security.results.trend.new', { count: diff.new_count })}</span>
      )}
    </div>
  )
}

function FindingItem({
  finding,
}: {
  finding: NonNullable<Project['security_audit_metadata']>['categories'][string]['findings'][number]
}) {
  const { t } = useTranslation('conversations')
  const [showRemediation, setShowRemediation] = useState(false)

  return (
    <div className="text-xs space-y-1">
      <div className="flex items-start gap-2">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${SEVERITY_COLORS[finding.severity]}`}>
          {finding.severity}
        </span>
        <span className="font-medium leading-tight">{finding.title}</span>
      </div>
      {finding.file_path && (
        <div className="flex items-center gap-1 text-muted-foreground ml-6">
          <FileCode className="h-3 w-3 shrink-0" />
          <span className="font-mono truncate">
            {finding.file_path}
            {finding.line_number ? `:${finding.line_number}` : ''}
          </span>
        </div>
      )}
      {finding.remediation && (
        <div className="ml-6">
          <button
            onClick={() => setShowRemediation(!showRemediation)}
            className="text-blue-600 dark:text-blue-400 hover:underline text-[10px]"
          >
            {t('live.security.results.remediation')}
          </button>
          {showRemediation && (
            <p className="text-muted-foreground mt-1 text-[10px] leading-relaxed">
              {finding.remediation}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function AuditStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation('conversations')

  switch (status) {
    case 'running':
      return (
        <Badge variant="outline" className="text-blue-600 border-blue-300">
          <Loader2 className="h-3 w-3 animate-spin mr-1" />
          {t('live.security.status.running')}
        </Badge>
      )
    case 'completed':
      return (
        <Badge variant="outline" className="text-green-600 border-green-300">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          {t('live.security.status.completed')}
        </Badge>
      )
    case 'failed':
      return (
        <Badge variant="outline" className="text-destructive border-destructive/30">
          <XCircle className="h-3 w-3 mr-1" />
          {t('live.security.status.failed')}
        </Badge>
      )
    default:
      return null
  }
}

function CategoryStatusBadge({ status, count }: { status: string; count: number }) {
  const { t } = useTranslation('conversations')

  switch (status) {
    case 'pass':
      return (
        <span className="text-green-600 text-[10px] font-medium flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" /> {t('live.security.results.categoryStatus.pass')}
        </span>
      )
    case 'warn':
      return (
        <span className="text-yellow-600 text-[10px] font-medium flex items-center gap-1">
          <AlertTriangle className="h-3 w-3" /> {t('live.security.results.categoryStatus.warn', { count })}
        </span>
      )
    case 'fail':
    case 'error':
      return (
        <span className="text-red-600 text-[10px] font-medium flex items-center gap-1">
          <XCircle className="h-3 w-3" /> {t('live.security.results.categoryStatus.error')}
        </span>
      )
    case 'skipped':
      return (
        <span className="text-muted-foreground text-[10px] font-medium">
          {t('live.security.results.categoryStatus.skipped')}
        </span>
      )
    default:
      return null
  }
}
